import Icon from './icons';

export const pageContent = {
  home: {
    title: 'Databuilder — AI & Data Experts for the UK Public Sector and Private Sector',
    description:
      'Databuilder: expert AI and data consultancy for UK local government, the public sector and financial services — plus EPC Complete, the leading synthetic property data platform.',
    footerTitle: 'Ready to move your AI agenda forward?',
    footerText: <>Strategic guidance, interim expertise or better property data — let’s talk.</>,
    footerPrimaryLabel: 'Get in touch',
    footerPrimaryHref: 'mailto:hello@databuilder.co.uk?subject=Tell%20me%20more%20about%20Databuilder',
    footerSecondaryLabel: 'Our services',
    footerSecondaryHref: '/consultancy',
    hero: {
      eyebrow: 'AI · Data Strategy · Public Sector Expertise',
      titlePrefix: 'Turning public sector data into ',
      titleAccent: 'confident decisions',
      titleSuffix: '.',
      description:
        'Practical AI strategy, expert leadership and proprietary data tools for UK local government, the public sector and financial services.',
      badges: [
        'Local Government',
        'Housing & ALMOs',
        'NHS & Health',
        'Fire and Rescue',
        'Financial Services',
        'HM Government Expert',
      ],
      goldBadges: ['HM Government Expert'],
      primaryLabel: 'Explore consultancy',
      primaryHref: '/consultancy',
      secondaryLabel: 'EPC Complete →',
      secondaryHref: '/epc',
    },
    cards: {
      why: [
        {
          icon: <Icon name="landmark" />,
          title: 'HM Government recognised expert',
          description: 'Industry Expert and Local Government Sector Expert in AI and data.',
        },
        {
          icon: <Icon name="tool" />,
          title: '10+ years delivering AI and data solutions',
          description: 'Real-world experience across local government, housing and financial services and government.',
        },
        {
          icon: <Icon name="award" />,
          title: 'Unique property and energy data expertise',
          description: 'Creators of EPC Complete, the UK\'s leading synthetic EPC dataset.',
        },
        {
          icon: <Icon name="briefcase" />,
          title: 'From strategy to implementation',
          description: 'Advisory, interim leadership and hands-on technical delivery in one partner.',
        },
      ],
      innovation: [
        {
          stage: 'Adopt',
          title: 'Use AI well',
          description: 'Put today’s AI tools to work safely inside your existing services — quick wins with proper guardrails.',
          items: ['Copilot and LLM tool rollout', 'AI usage policy and governance', 'Staff capability and training'],
        },
        {
          stage: 'Augment',
          title: 'Redesign around AI',
          description: 'Rebuild individual services with AI in the loop — automating casework, triage and insight at scale.',
          items: ['Intelligent process automation', 'Custom models and RAG pipelines', 'Decision support for frontline teams'],
        },
        {
          stage: 'AI-first',
          title: 'Rebuild AI-first',
          description: 'Reimagine the operating model from the ground up — services designed natively around AI, data and automation.',
          items: ['AI-first service architecture', 'Agentic workflows and orchestration', 'New operating model design'],
        },
      ],
      services: [
        {
          title: 'Consultancy & Interim Leadership',
          description: 'Strategic advisory, interim CDO/CDAO roles and hands-on delivery for data and AI programmes.',
          items: ['AI strategy and roadmaps', 'Data governance and architecture', 'Interim Chief Data / AI Officer', 'AI procurement assurance'],
          ctaLabel: 'View consultancy →',
          ctaHref: '/consultancy',
          ctaClassName: 'button blue',
        },
        {
          title: 'EPC Complete',
          description: 'The UK’s leading synthetic EPC dataset — built for net zero planning across housing, government and finance.',
          items: ['AI-generated, privacy-safe EPC data', 'Net zero scenario modelling', 'Property level granularity', 'API-first integration'],
          ctaLabel: 'Explore EPC Complete →',
          ctaHref: '/epc',
          ctaClassName: 'button blue',
        },
      ],
      stats: [
        { value: '10+ years', label: 'in data science, AI strategy and delivery' },
        { value: 'HM Government', label: 'recognised Industry and Sector Expert' },
        { value: 'Public & private', label: 'local government through to banking' },
        { value: 'Outcome-led', label: 'strategy through to production' },
      ],
    },
    marquee: [
      'AI strategy and roadmaps',
      'Custom model development and RAG pipelines',
      'Agents and agentic AI workflow design',
      'Interim Chief Data / AI Officer',
      'Data governance and architecture',
      'AI procurement assurance',
      'Agentic workflows and orchestration',
      'Synthetic property data',
      'Net zero scenario modelling',
      'Staff capability and training',
    ],
    sections: {
      why: {
        label: 'Why Databuilder',
        title: 'No noise. Just outcomes.',
        description: 'We work where it matters: complex organisations, legacy systems, real delivery pressure.',
      },
      innovation: {
        label: 'Innovation',
        title: 'From using AI to being AI-first',
        description: 'Wherever you are on the journey, we meet you there — and take you further.',
      },
      services: {
        label: 'Services',
        title: 'How we work with you',
        description: 'Flexible engagements — strategic advisory to embedded delivery.',
      },
    },
  },
  epc: {
    title: 'Databuilder — EPC Complete',
    description: "EPC Complete is the UK's most complete synthetic EPC dataset for councils, housing organisations and financial services.",
    footerTitle: 'Ready to model the UK’s retrofit challenge?',
    footerText: <>The data to plan, prioritise and act — without the governance headaches.</>,
    footerPrimaryLabel: 'Request a demo',
    footerPrimaryHref: 'mailto:hello@databuilder.co.uk?subject=EPC%20Complete%20Demo%20Request',
    footerSecondaryLabel: 'View access models',
    footerSecondaryHref: '#pricing',
    hero: {
      eyebrow: 'Synthetic Property Data · UK EPC · AI-Powered',
      titlePrefix: 'The UK’s most complete ',
      titleAccent: 'synthetic EPC dataset',
      titleSuffix: '.',
      description:
        'High-fidelity synthetic Energy Performance Certificate data at scale — so councils, emergency services, housing bodies, insurers and banks can plan, model and analyse with confidence.',
      badges: ['Local Government', 'Housing Associations', 'Emergency Services', 'Financial Services', 'Climate Change & Net Zero'],
      goldBadges: ['Climate Change & Net Zero'],
      primaryLabel: 'Request sample data',
      primaryHref: '#pricing',
      secondaryLabel: 'Discuss your use case',
      secondaryHref: 'mailto:hello@databuilder.co.uk?subject=EPC%20Complete%20Enquiry',
    },
    cards: {
      features: [
        {
          icon: <Icon name="chart" />,
          title: 'Authentic UK distributions',
          description: 'Generated from real EPC register patterns across all local authority areas, property types and tenure types. Statistically representative at national and local scale.',
        },
        {
          icon: <Icon name="lock" />,
          title: 'Official EPC data, enhanced by AI',
          description: 'Richer property and energy intelligence for net zero planning, investment decisions and portfolio analysis.',
        },
        {
          icon: <Icon name="zap" />,
          title: 'Net zero scenario modelling',
          description: 'Model retrofit pathways, energy efficiency upgrades and carbon reduction trajectories across any geography or housing stock profile. Built for net zero planning.',
        },
        {
          icon: <Icon name="link" />,
          title: 'API-first integration',
          description: 'Integrate directly into your existing data pipelines, analytics platforms and modelling environments. Structured outputs compatible with modern data stacks.',
        },
      ],
      useCases: [
        {
          icon: <Icon name="landmark" />,
          title: 'Local Authorities & Combined Authorities',
          description: 'Plan retrofit programmes, model housing stock decarbonisation and meet net zero obligations with confidence.',
          items: ['Housing stock EPC profiling', 'Retrofit cost modelling', 'Fuel poverty analysis', 'Net zero pathway planning'],
        },
        {
          icon: <Icon name="home" />,
          title: 'Housing Associations & ALMOs',
          description: 'Build smarter asset management strategies and prioritise decarbonisation investment across complex, mixed-tenure portfolios.',
          items: ['Asset register enrichment', 'Retrofit prioritisation', 'Decent Homes compliance', 'Sustainability reporting'],
        },
        {
          icon: <Icon name="briefcase" />,
          title: 'Banks & Financial Services',
          description: 'Assess green mortgage exposure, stress-test property portfolios and meet climate risk regulatory requirements.',
          items: ['Green mortgage product development', 'Climate risk stress testing', 'TCFD / Basel III compliance', 'Portfolio EPC distribution analysis'],
        },
        {
          icon: <Icon name="building" />,
          title: 'Central Government & ALBs',
          description: 'Underpin policy analysis, national retrofit modelling and housing strategy with high-quality synthetic data at national scale.',
          items: ['Policy impact modelling', 'National retrofit analysis', 'Analytical environment testing', 'Research and development'],
        },
        {
          icon: <Icon name="fireTruck" />,
          title: 'Fire & Rescue Services',
          description: 'Identify building characteristics, prioritise risk assessments and support evidence-based prevention programmes.',
          items: ['High-risk building identification', 'Resource planning', 'Prevention and inspection programmes', 'Community risk management'],
        },
      ],
      product: [
        {
          icon: <Icon name="cpu" />,
          title: 'AI-powered generation',
          description: 'Advanced generative models trained on real UK EPC distributions ensure statistical fidelity at every level of granularity.',
        },
        {
          icon: <Icon name="map" />,
          title: 'Local authority granularity',
          description: 'Data structured by UPRN, MSOA, ward and local authority boundary — ready for geospatial analysis and reporting.',
        },
        {
          icon: <Icon name="trendingUp" />,
          title: 'Full EPC schema coverage',
          description: 'All standard EPC fields including energy rating, floor area, construction type, heating system, tenure and more.',
        },
        {
          icon: <Icon name="refresh" />,
          title: 'Continuous refresh',
          description: 'Regular model retraining against updated register patterns keeps your synthetic dataset current and representative.',
        },
        {
          icon: <Icon name="package" />,
          title: 'Bulk export & API',
          description: 'Access data via REST API or scheduled bulk export in CSV, Parquet or JSON formats — compatible with leading data platforms.',
        },
        {
          icon: <Icon name="shield" />,
          title: 'Transparent and trusted ',
          description: 'Clear methodology, documented assumptions and traceable data sources help organisations use EPC Complete with confidence.',
        },
      ],
      pricing: [
        {
          title: 'Evaluation',
          description: 'For organisations validating a use case, testing data compatibility or developing an initial proof of concept.',
          items: ['Representative sample dataset', 'Documentation and field definitions', 'Limited geographic coverage', 'Technical walkthrough', 'Clear route to production access'],
          ctaLabel: 'Request an evaluation',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=EPC%20Complete%20Evaluation%20Request',
          ctaClassName: 'button blue',
        },
        {
          title: 'Operational',
          description: 'For organisations using EPC Complete in ongoing planning and modelling.',
          items: ['Agreed geographic or portfolio coverage', 'Bulk data delivery or API access', 'Scheduled refreshes', 'Implementation support', 'Standard support and licensing'],
          ctaLabel: 'Discuss operational access',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=EPC%20Complete%20Operational%20Access',
          ctaClassName: 'button primary',
        },
        {
          title: 'Enterprise',
          description: 'For national analysis, complex integrations, financial modelling and regulated environments.',
          items: ['Large-scale or national coverage', 'Configurable delivery and refresh schedules', 'Integration and technical support', 'Enhanced assurance and service options', 'Commercial terms tailored to organisational use'],
          ctaLabel: 'Talk to us about enterprise access',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=EPC%20Complete%20Enterprise%20Access',
          ctaClassName: 'button blue',
        },
      ],
    },
    sections: {
      features: {
        label: 'Why EPC Complete',
        title: 'Real EPC data has limits. EPC Complete doesn’t.',
        description: 'The national register is incomplete, inconsistent and limited for bulk use. EPC Complete gives you the data you need — without the privacy risk.',
      },
      useCases: {
        label: 'Use Cases',
        title: 'Who uses EPC Complete',
        description: 'Built for the organisations shaping the UK’s climate change, net zero and housing agenda.',
      },
      product: {
        label: 'Product features',
        title: 'Everything you need. Nothing you don’t.',
        description: 'Engineered for analytical rigour and operational simplicity.',
      },
      pricing: {
        label: 'Access models',
        title: 'Flexible access. Scaled to your requirements.',
        description:
          'From initial evaluation to national-scale modelling, access is tailored to your required coverage, delivery method and refresh frequency. We will recommend the simplest option that meets your analytical and operational needs.',
      },
      pricingNote: {
        title: 'How pricing works',
        body:
          'Pricing is based on the coverage, delivery method, refresh frequency and level of support you need. After a short discovery call, we will provide a clear written recommendation and quote.',
        signal: 'Paid evaluations available for defined use cases.',
        qualifier: 'Designed for organisational and programme-level use rather than individual property lookups.',
      },
    },
  },
  about: {
    title: 'Databuilder — About',
    description: 'Learn why Databuilder exists and the expertise behind the company.',
    footerTitle: 'Want to know how we can help?',
    footerText: <>Always happy to have an exploratory conversation — no pitch, no pressure.</>,
    footerPrimaryLabel: 'Get in touch',
    footerPrimaryHref: 'mailto:hello@databuilder.co.uk?subject=Enquiry',
    footerSecondaryLabel: 'Our services',
    footerSecondaryHref: '/consultancy',
    hero: {
      eyebrow: 'About Databuilder',
      titlePrefix: 'Built on real expertise. Focussed on ',
      titleAccent: 'real outcomes',
      titleSuffix: '.',
      description:
        'The public sector deserves the same quality of data and AI expertise the best private firms take for granted. That’s why Databuilder exists.',
      badges: [],
      primaryLabel: 'Our services',
      primaryHref: '/consultancy',
      secondaryLabel: 'Get in touch',
      secondaryHref: 'mailto:hello@databuilder.co.uk?subject=Enquiry',
    },
    sections: {
      story: {
        label: 'Our story',
        title: 'Why Databuilder exists',
      },
      founder: {
        label: 'Founder',
        title: 'The expertise behind Databuilder',
      },
      principles: {
        label: 'How we work',
        title: 'What you can expect',
        description: 'The same principles, every engagement.',
      },
    },
    cards: {
      principles: [
        {
          icon: <Icon name="target" />,
          title: 'Outcomes, not outputs',
          description:
            'We measure success by the difference we make, not the documents we produce. Every engagement is anchored to what actually changes for your organisation.',
        },
        {
          icon: <Icon name="search" />,
          title: 'Honest assessment',
          description:
            'We’ll tell you what’s realistic, what’s not and where the real risks are — even when that’s not what you want to hear. Credibility matters more than comfort.',
        },
        {
          icon: <Icon name="users" />,
          title: 'Genuine partnership',
          description:
            'We work with your team, not around them. Knowledge transfer and capability building are built into every engagement by default.',
        },
        {
          icon: <Icon name="scales" />,
          title: 'Responsible by default',
          description:
            'We take responsible AI seriously — not as a compliance exercise but as a core professional standard. Ethics, transparency and fairness are non-negotiable.',
        },
      ],
    },
  },
  consultancy: {
    title: 'Databuilder — Consultancy',
    description:
      'Agentic AI, AI agents and responsible AI consultancy for organisations looking to improve efficiency with EU AI Act-ready governance.',
    footerTitle: 'Let’s talk about what you’re trying to achieve.',
    footerText: <>A defined project or still working out what good looks like — happy to have the conversation.</>,
    footerPrimaryLabel: 'Get in touch',
    footerPrimaryHref: 'mailto:hello@databuilder.co.uk?subject=Consultancy%20Enquiry',
    footerSecondaryLabel: 'About Databuilder',
    footerSecondaryHref: '/about',
    hero: {
      eyebrow: 'AI · Data Strategy · Interim Leadership',
      titlePrefix: 'AI and data consultancy that ',
      titleAccent: 'delivers',
      titleSuffix: '.',
      description:
        'Strategic advisory to embedded delivery — the technical depth and sector understanding to turn ambition into outcomes.',
      badges: ['Local Government', 'Central Government', 'NHS & Health', 'Housing', 'Banking & Finance'],
      primaryLabel: 'Our services',
      primaryHref: '#services',
      secondaryLabel: 'Get in touch',
      secondaryHref: 'mailto:hello@databuilder.co.uk?subject=Consultancy%20Enquiry',
    },
    cards: {
      services: [
        {
          title: 'AI Strategy & Roadmaps',
          description: 'From opportunity identification to board-ready AI strategy. We assess your data maturity, identify the right use cases and build a prioritised, deliverable roadmap.',
          items: ['Data and AI maturity assessment', 'Use case identification and prioritisation', 'Board and executive-level strategy', 'Technology selection guidance'],
        },
        {
          title: 'Interim Chief Data / AI Officer',
          description: 'Senior on-demand leadership for critical data and AI programmes — strategic direction, stakeholder confidence and delivery oversight without the permanent hire.',
          items: ['Interim CDO / CDAO roles', 'Programme governance and oversight', 'Senior stakeholder engagement', 'Knowledge transfer and team development'],
        },
        {
          title: 'Data Governance & Architecture',
          description: 'The foundations that make AI initiatives actually work. We design governance structures, data architectures and operating models that turn potential into sustainable capability.',
          items: ['Data governance frameworks', 'Architecture design and review', 'Data quality and lineage', 'Policy and standards development'],
        },
        {
          title: 'AI Procurement & Assurance',
          description: 'Independent technical review of AI supplier proposals, vendor assessments and procurement specifications — so you buy the right thing for the right reasons.',
          items: ['AI vendor assessment and due diligence', 'Technical specification review', 'ITT and tender advisory', 'Post-procurement assurance'],
        },
        {
          title: 'Net Zero Data Strategy',
          description: 'Connecting your data assets to decarbonisation targets. We help housing, local authority and financial services teams build data infrastructure to plan and deliver net zero.',
          items: ['EPC and retrofit data strategy', 'Carbon baseline and trajectory analysis', 'Climate risk data frameworks', 'Net zero reporting and accountability'],
        },
        {
          title: 'AI Implementation Support',
          description: 'Bridging the gap between strategy and delivery. Hands-on technical support to embed AI tools, models and pipelines into your organisation’s day-to-day operations.',
          items: ['Model development and validation', 'MLOps and production deployment', 'Data pipeline engineering', 'Responsible AI frameworks'],
        },
      ],
      sectors: [
        {
          icon: <Icon name="landmark" />,
          title: 'Local Government',
          description: 'Recognised Sector Expertise for local government. We understand the governance, procurement and operational pressures councils face when delivering data and AI programmes.',
        },
        {
          icon: <Icon name="building" />,
          title: 'Central Government & ALBs',
          description: 'Experience supporting arm’s-length bodies and central government teams on data strategy, AI adoption and analytical capability development.',
        },
        {
          icon: <Icon name="activity" />,
          title: 'NHS & Health',
          description: 'Data strategy and AI advisory for health and care organisations navigating information governance, interoperability and analytical challenges at scale.',
        },
        {
          icon: <Icon name="home" />,
          title: 'Housing & Social Care',
          description: 'Asset management, retrofit planning and net zero strategy for housing associations, ALMOs and registered providers with complex property portfolios.',
        },
        {
          icon: <Icon name="briefcase" />,
          title: 'Banking & Financial Services',
          description: 'AI adoption, climate risk data and property analytics for banks and financial institutions operating in regulated, data-intensive environments.',
        },
      ],
      work: [
        {
          title: 'Outcomes, not outputs',
          description: 'We measure success by the difference we make, not the documents we produce. Every engagement is anchored to what actually changes for your organisation.',
        },
        {
          title: 'Honest assessment',
          description: 'We’ll tell you what’s realistic, what’s not and where the real risks are — even when that’s not what you want to hear. Credibility matters more than comfort.',
        },
        {
          title: 'Genuine partnership',
          description: 'We work with your team, not around them. Knowledge transfer and capability building are built into every engagement by default.',
        },
        {
          title: 'Responsible by default',
          description: 'We take responsible AI seriously — not as a compliance exercise but as a core professional standard. Ethics, transparency and fairness are non-negotiable.',
        },
      ],
      engagement: [
        {
          title: 'Project-based',
          price: 'Fixed price',
          description: 'Defined deliverables, clear scope and a fixed timeline. Ideal for specific use cases, assessments or strategy pieces.',
          items: ['Scoped deliverables and milestones', 'Fixed timeline and budget', 'Dedicated senior resource', 'Clear success criteria'],
          ctaLabel: 'Discuss a project',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Project%20Enquiry',
          ctaClassName: 'button blue',
        },
        {
          title: 'Strategic retainer',
          price: 'Flexible monthly engagement',
          featuredBadge: 'Most popular',
          description: 'Ongoing access to senior AI and data expertise, with a level of support shaped around your programme, internal capability and delivery needs.',
          items: ['Regular strategic advisory sessions', 'Priority access to senior expertise', 'Flexible resource allocation', 'Escalation support and review', 'Monthly progress and impact reporting'],
          ctaLabel: 'Discuss your priorities',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Retainer%20Enquiry',
          ctaClassName: 'button primary',
        },
        {
          title: 'Interim leadership',
          price: 'Bespoke',
          description: 'Senior interim CDO or CDAO support for critical periods — restructures, programme delivery, transformation or capability gaps.',
          items: ['Experienced senior leadership', 'Full stakeholder and board engagement', 'Structured knowledge transfer', 'Team capability development'],
          ctaLabel: 'Talk to us',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Interim%20Leadership%20Enquiry',
          ctaClassName: 'button blue',
        },
      ],
    },
    sections: {
      services: {
        label: 'What we do',
        title: 'Services built for how the public sector actually works',
        description: 'No imposed frameworks. We work with your constraints, your governance, your teams.',
      },
      sectors: {
        label: 'Sectors',
        title: 'Deep in the sectors we know best',
        description: 'Focussed expertise, not generalist consulting.',
      },
      work: {
        label: 'How we work',
        title: 'Rigorous. Practical by design.',
        description: 'Structured around real delivery, not theoretical best practice.',
      },
      engagement: {
        label: 'Engagement models',
        title: 'Choose how you work with us',
        description: 'Projects, retainers or interim leadership — we flex to fit.',
      },
    },
  },
  agents: {
    title: 'Databuilder — Agentic AI & AI Agents',
    description:
      'We design, build and deliver AI agents and agentic AI systems — production-ready solutions with EU AI Act-ready governance and measurable efficiency gains.',
    footerTitle: 'Have a problem an AI agent could solve?',
    footerText: <>Tell us about the process problem, service bottleneck or efficiency target — we’ll design, build and deliver the solution.</>,
    footerPrimaryLabel: 'Get in touch',
    footerPrimaryHref: 'mailto:hello@databuilder.co.uk?subject=Agentic%20AI%20Enquiry',
    footerSecondaryLabel: 'Engagement models',
    footerSecondaryHref: '#engagement',
    hero: {
      eyebrow: 'Agentic AI · AI Agents · EU AI Act readiness',
      titlePrefix: 'We design and build AI agents and agentic AI systems that ',
      titleAccent: 'solve real problems',
      titleSuffix: '.',
      description:
        'From first workshop to working software in production — we build, deploy and support agent systems that remove operational friction and deliver measurable efficiency, with governance built in from the start.',
      badges: ['Public Sector', 'Operational Efficiency', 'Risk & Governance', 'EU AI Act aligned', 'Private Sector'],
      goldBadges: ['EU AI Act aligned'],
      primaryLabel: 'See the difference',
      primaryHref: '#difference',
      secondaryLabel: 'Discuss a problem',
      secondaryHref: 'mailto:hello@databuilder.co.uk?subject=Agentic%20AI%20Enquiry',
      comparison: {
        badge: 'EU AI Act by design',
        title: 'Specialising in both AI agents and agentic AI',
        description: 'We start with the problem, map the workflow, choose the right level of autonomy — then build and deliver the system.',
        cards: [
          {
            kicker: 'AI Agent',
            title: 'One task, one outcome',
            description: 'A bounded system that completes a defined job — triage, retrieval, drafting or workflow execution.',
          },
          {
            kicker: 'Agentic AI',
            title: 'Multiple steps, coordinated action',
            description: 'An architecture that plans, sequences and orchestrates tools or agents to achieve a broader goal.',
            accent: true,
          },
        ],
      },
    },
    cards: {
      difference: [
        {
          stage: 'AI Agent',
          title: 'Execute a specific task',
          description: 'An AI agent is usually a focussed system built to complete a narrow, well-defined job with clear inputs, tool access and success criteria.',
          items: ['Useful for repeatable tasks and process support', 'Best when the workflow is already understood', 'Often the fastest route to efficiency gains'],
        },
        {
          stage: 'Agentic AI',
          title: 'Coordinate multi-step work',
          description: 'Agentic AI is broader: a system that plans, chooses tools, delegates work and adapts across several steps to pursue an outcome.',
          items: ['Useful for more complex operational change', 'May combine several agents and shared memory', 'Needs stronger guardrails and oversight'],
        },
        {
          stage: 'Our approach',
          title: 'Problem first, then we build the right pattern',
          description: 'We choose the smallest, safest and most effective automation pattern — whether that is a single AI agent or a broader agentic system — then build, deploy and support it.',
          items: ['Outcome-led discovery and process mapping', 'Hands-on build, deployment and support', 'EU AI Act aligned documentation and controls'],
        },
      ],
      help: [
        {
          title: 'Problem framing & use case selection',
          description: 'We start with the operational pain point, map the process and identify the right automation pattern for the job.',
          items: ['Workflow analysis and bottleneck identification', 'Use case prioritisation and value sizing', 'Task suitability for AI agent or agentic AI', 'Delivery roadmap and business case'],
        },
        {
          title: 'Agent design, build & orchestration',
          description: 'We build the AI agents that do the work, and the agentic workflows that coordinate multiple steps, tools or agents safely — production-grade software, not prototypes.',
          items: ['Prompt, tool and memory design', 'Workflow orchestration and routing', 'Integration with existing systems', 'Testing, evaluation and deployment'],
        },
        {
          title: 'EU AI Act & responsible AI assurance',
          description: 'European standards are not an afterthought. We design governance, controls and documentation into the solution from day one.',
          items: ['Risk identification and control mapping', 'Human oversight and accountability design', 'Transparency, logs and documentation', 'Policy and procurement alignment'],
        },
        {
          title: 'Production delivery & efficiency uplift',
          description: 'We take the solution into production ourselves, then measure whether it is actually saving time, reducing cost or improving service.',
          items: ['Pilot build and production deployment', 'Efficiency and quality measurement', 'Adoption support and knowledge transfer', 'Continuous improvement and scale-up'],
        },
      ],
      standards: [
        {
          icon: <Icon name="shield" />,
          title: 'Risk classification',
          description: 'We help you assess the use case, the level of autonomy and the controls required before a solution goes live.',
        },
        {
          icon: <Icon name="clipboard" />,
          title: 'Documentation and traceability',
          description: 'From model cards to decision logs, we build the artefacts needed for operational confidence and auditability.',
        },
        {
          icon: <Icon name="users" />,
          title: 'Human oversight',
          description: 'We design human-in-the-loop checks so people stay accountable for decisions where the stakes are high.',
        },
        {
          icon: <Icon name="link" />,
          title: 'Procurement and assurance',
          description: 'We support teams evaluating suppliers, designing specifications and making sure compliance is embedded in delivery.',
        },
      ],
      work: [
        {
          title: 'Understand the problem',
          description: 'We identify the workflow, the pain points and the measurable outcome you want to improve.',
        },
        {
          title: 'Design the right system',
          description: 'We choose between an AI agent, a multi-agent pattern or a broader agentic AI architecture.',
        },
        {
          title: 'Build and deploy',
          description: 'We build the system ourselves — tested, evaluated and governed with EU AI Act aligned controls — and take it into production.',
        },
        {
          title: 'Measure efficiency',
          description: 'We track whether the new capability saves time, improves quality and reduces operational effort.',
        },
      ],
      engagement: [
        {
          title: 'Discovery sprint',
          price: 'Fixed price',
          description: 'Rapid problem framing, opportunity sizing and a practical recommendation for the best automation pattern.',
          items: ['Workflow mapping and use case selection', 'Short delivery timeline', 'Clear next-step recommendation', 'Outcome and risk assessment'],
          ctaLabel: 'Discuss a sprint',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Discovery%20Sprint%20Enquiry',
          ctaClassName: 'button blue',
        },
        {
          title: 'Build and scale',
          price: 'Phased delivery',
          featuredBadge: 'Most popular',
          description: 'We build the solution in defined phases — validate the outcome in production, then scale your investment as the capability proves its value.',
          items: ['Working software delivered each phase', 'Architecture and governance built in', 'Prioritised build and iteration backlog', 'Monthly impact and efficiency reporting', 'EU AI Act readiness support'],
          ctaLabel: 'Start a build',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Build%20and%20Scale%20Enquiry',
          ctaClassName: 'button primary',
        },
        {
          title: 'Assurance review',
          price: 'Bespoke',
          description: 'Independent review of an existing AI agent or agentic AI solution — quality, governance, risk and readiness to scale.',
          items: ['Solution and risk review', 'Governance and control assessment', 'Documentation and evidence check', 'Recommendations for improvement'],
          ctaLabel: 'Request a review',
          ctaHref: 'mailto:hello@databuilder.co.uk?subject=Assurance%20Review%20Enquiry',
          ctaClassName: 'button blue',
        },
      ],
    },
    sections: {
      difference: {
        label: 'Difference',
        title: 'AI Agent vs Agentic AI',
        description: 'We specialise in both, but they solve different problems and need different levels of design, control and governance.',
      },
      help: {
        label: 'How we deliver',
        title: 'From problem to production — we build the solution',
        description: 'Not just advice: we design, build, deploy and support working agent systems that remove bottlenecks and improve service quality.',
      },
      standards: {
        label: 'European standards',
        title: 'Built for the EU AI Act and practical governance',
        description: 'We help organisations adopt agentic AI without losing sight of transparency, accountability and human control.',
      },
      work: {
        label: 'How we work',
        title: 'Focussed on one problem, one team and one measurable improvement',
        description: 'We keep the engagement tight enough to move quickly, but rigorous enough to stand up to scrutiny.',
      },
      engagement: {
        label: 'Engagement models',
        title: 'Choose the shape that fits the problem',
        description: 'From a focussed discovery to ongoing support, we adapt to the pace and risk profile of your organisation.',
      },
    },
  },
};
